/**
 * LaydownComparisonPage — Phase 2 page
 * ─────────────────────────────────────────────────────────────────────────────
 * Side-by-side comparison of two BaleSelectionRun records. QC uses this to
 * learn which mix worked better — same recipe, different inventory choices,
 * different verdict outcomes.
 *
 * Layout:
 *   Top:    Two run pickers (left + right), each loading available runs
 *           via baleSelectionApi.listRuns({plant_id})
 *   Middle: Twin headline cards — VerdictBadge + KPI tiles
 *   Bottom: Comparison rows for SCI, Mic, plus_b, length, uniformity:
 *           - Mean / SD / CV% / Min / Max for both sides
 *           - Drift status pill if applicable
 *
 * No new backend needed — uses existing /bale-selection/runs/{id}.
 *
 * Spec: blueprint Sec 22-23 (laydown comparison for learning loop).
 */
import { useState } from 'react'
import {
  Alert, Card, Col, Empty, Row, Select, Space, Statistic, Table,
  Tag, Typography,
} from 'antd'
import { SwapOutlined, CalendarOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import PageHeader from '@/shared/components/PageHeader'
import VerdictBadge from '@/shared/components/VerdictBadge'
import { baleSelectionApi } from '@/features/baleManagement/api'
import { plantsApi } from '@/features/masters/plants/api'

const { Text, Title } = Typography

// ── Types (subset of BaleSelectionRunResponse) ──────────────────────────────
type WithinVar = {
  mean: number; sd: number; cv_pct: number;
  min: number; max: number; n: number;
}
type DriftEntry = {
  current: number; previous: number; delta: number;
  limit: number | null; within: boolean; unit?: string;
}
type Run = {
  id: string
  yarn_recipe_id: string
  target_bales: number
  selected_bale_ids: Array<{ bale_id: string }> | null
  within_band: string | null
  drift_status: string | null
  warnings: string[]
  status: string
  created_at: string
  approved_at: string | null
  within_variance_json: Record<string, WithinVar> | null
  drift_json: Record<string, DriftEntry> | null
}

type RunListItem = {
  id: string
  status: string
  created_at: string
  yarn_recipe_id: string
  target_bales: number
  within_band: string | null
}

type Plant = { id: string; name: string }

const PROPERTIES = [
  { key: 'sci',         label: 'SCI',          unit: '' },
  { key: 'mic',         label: 'Mic',          unit: '' },
  { key: 'plus_b',      label: '+b',           unit: '' },
  { key: 'uhml_mm',     label: 'UHML',         unit: 'mm' },
  { key: 'uniformity',  label: 'Uniformity',   unit: '%' },
] as const

const DRIFT_TAG_COLOR: Record<string, string> = {
  WITHIN_LIMITS: 'green',
  EXCEEDS_LIMITS: 'red',
  NO_PRIOR: 'default',
  UNKNOWN: 'default',
}

// ── Run picker dropdown ──────────────────────────────────────────────────────
function RunPicker({
  label, value, runs, loading, onChange,
}: {
  label: string
  value: string | undefined
  runs: RunListItem[]
  loading: boolean
  onChange: (v: string) => void
}) {
  return (
    <Card size="small" styles={{ body: { padding: 12 } }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Text strong>{label}</Text>
        <Select
          value={value}
          onChange={onChange}
          loading={loading}
          placeholder="Pick a run…"
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
          options={runs.map(r => ({
            label: `${dayjs(r.created_at).format('DD MMM HH:mm')} · ${r.status} · ${r.target_bales} bales · ${r.within_band ?? '—'}`,
            value: r.id,
          }))}
        />
      </Space>
    </Card>
  )
}

// ── Run summary (twin card content) ──────────────────────────────────────────
function RunSummary({ run, side }: { run: Run | undefined; side: 'L' | 'R' }) {
  if (!run) {
    return (
      <Card size="small" style={{ minHeight: 200 }}>
        <Empty description="Pick a run on the left" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Card>
    )
  }
  const baleCount = run.selected_bale_ids?.length ?? 0
  const wv = run.within_variance_json ?? {}
  return (
    <Card
      size="small"
      title={
        <Space size={8}>
          <Tag color={side === 'L' ? 'blue' : 'purple'}>{side === 'L' ? 'A' : 'B'}</Tag>
          <Text strong style={{ fontSize: 13 }}>
            {dayjs(run.created_at).format('DD MMM YYYY · HH:mm')}
          </Text>
          <Tag>{run.status}</Tag>
        </Space>
      }
    >
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <VerdictBadge band={run.within_band ?? null} size="full" baleCount={baleCount} />
        </Col>
        <Col xs={12}>
          <Statistic
            title="Avg Mic"
            value={wv.mic?.mean != null ? Number(wv.mic.mean).toFixed(2) : '—'}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col xs={12}>
          <Statistic
            title="Mic CV"
            value={wv.mic?.cv_pct != null ? Number(wv.mic.cv_pct).toFixed(2) : '—'}
            suffix={wv.mic?.cv_pct != null ? '%' : ''}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col xs={12}>
          <Statistic
            title="Avg SCI"
            value={wv.sci?.mean != null ? Number(wv.sci.mean).toFixed(0) : '—'}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col xs={12}>
          <Statistic
            title="SCI CV"
            value={wv.sci?.cv_pct != null ? Number(wv.sci.cv_pct).toFixed(2) : '—'}
            suffix={wv.sci?.cv_pct != null ? '%' : ''}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={24}>
          <Space size={6} wrap>
            <CalendarOutlined style={{ color: '#888' }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Drift:
            </Text>
            <Tag color={DRIFT_TAG_COLOR[run.drift_status ?? 'UNKNOWN']}>
              {run.drift_status ?? 'UNKNOWN'}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              · Bales {baleCount}/{run.target_bales}
            </Text>
          </Space>
        </Col>
        {run.warnings.length > 0 && (
          <Col span={24}>
            <Alert
              type="warning"
              showIcon
              message={`${run.warnings.length} warning${run.warnings.length === 1 ? '' : 's'}`}
              description={
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {run.warnings.slice(0, 3).map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              }
            />
          </Col>
        )}
      </Row>
    </Card>
  )
}

// ── Property comparison row ──────────────────────────────────────────────────
type PropRow = {
  key: string
  label: string
  unit: string
  l_mean: string; l_cv: string; l_min: string; l_max: string
  r_mean: string; r_cv: string; r_min: string; r_max: string
  delta_mean: number | null
  better_side: 'L' | 'R' | 'TIE' | null   // who has the lower CV%
}

function buildRows(left: Run | undefined, right: Run | undefined): PropRow[] {
  const fmt = (n: number | undefined | null, decimals = 2) =>
    n == null ? '—' : Number(n).toFixed(decimals)

  return PROPERTIES.map(p => {
    const lwv = left?.within_variance_json?.[p.key]
    const rwv = right?.within_variance_json?.[p.key]
    const l_mean = fmt(lwv?.mean)
    const r_mean = fmt(rwv?.mean)
    const l_cv = fmt(lwv?.cv_pct)
    const r_cv = fmt(rwv?.cv_pct)
    const delta_mean = lwv?.mean != null && rwv?.mean != null
      ? Number(rwv.mean) - Number(lwv.mean)
      : null

    let better_side: PropRow['better_side'] = null
    if (lwv?.cv_pct != null && rwv?.cv_pct != null) {
      if (Math.abs(Number(lwv.cv_pct) - Number(rwv.cv_pct)) < 0.05) better_side = 'TIE'
      else better_side = Number(lwv.cv_pct) < Number(rwv.cv_pct) ? 'L' : 'R'
    }

    return {
      key: p.key,
      label: p.label,
      unit: p.unit,
      l_mean, l_cv, l_min: fmt(lwv?.min), l_max: fmt(lwv?.max),
      r_mean, r_cv, r_min: fmt(rwv?.min), r_max: fmt(rwv?.max),
      delta_mean, better_side,
    }
  })
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function LaydownComparisonPage() {
  const [plantId, setPlantId] = useState<string | undefined>()
  const [leftRunId, setLeftRunId] = useState<string | undefined>()
  const [rightRunId, setRightRunId] = useState<string | undefined>()

  const plantsQ = useQuery<Plant[]>({
    queryKey: ['plants', 'all'],
    queryFn: async () => {
      const res = await plantsApi.list()
      const r = res as unknown as { items?: Plant[]; data?: Plant[] } | Plant[]
      if (Array.isArray(r)) return r
      return r.items ?? r.data ?? []
    },
  })

  if (!plantId && plantsQ.data && plantsQ.data.length > 0) {
    setPlantId(plantsQ.data[0].id)
  }

  const runsQ = useQuery<RunListItem[]>({
    queryKey: ['bale-selection-runs', plantId],
    queryFn: () => baleSelectionApi.listRuns({ plant_id: plantId, limit: 50 }),
    enabled: Boolean(plantId),
  })

  const leftQ = useQuery<Run>({
    queryKey: ['bale-selection-run', leftRunId],
    queryFn: () => baleSelectionApi.getRun(leftRunId!),
    enabled: Boolean(leftRunId),
  })
  const rightQ = useQuery<Run>({
    queryKey: ['bale-selection-run', rightRunId],
    queryFn: () => baleSelectionApi.getRun(rightRunId!),
    enabled: Boolean(rightRunId),
  })

  const swap = () => {
    setLeftRunId(rightRunId)
    setRightRunId(leftRunId)
  }

  const rows = buildRows(leftQ.data, rightQ.data)

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title="Laydown Comparison"
        subtitle="Compare two bale-selection runs side-by-side to learn what worked"
        extra={
          <Space>
            <Select
              value={plantId}
              onChange={setPlantId}
              placeholder="Plant"
              style={{ width: 180 }}
              loading={plantsQ.isLoading}
              options={(plantsQ.data || []).map(p => ({ label: p.name, value: p.id }))}
            />
            <SwapOutlined
              onClick={swap}
              style={{ fontSize: 18, cursor: 'pointer' }}
              title="Swap A ↔ B"
            />
          </Space>
        }
      />

      {!plantId && (
        <Alert message="Pick a plant to begin." type="info" showIcon style={{ marginTop: 16 }} />
      )}

      {plantId && runsQ.data && runsQ.data.length === 0 && (
        <Alert
          message="No runs to compare yet"
          description="Run Quick Laydown or use the Suggest button on Laydown Planning to create runs."
          type="info" showIcon style={{ marginTop: 16 }}
        />
      )}

      {plantId && runsQ.data && runsQ.data.length > 0 && (
        <>
          {/* Run pickers */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <RunPicker
                label="Run A (left)"
                value={leftRunId}
                onChange={setLeftRunId}
                runs={runsQ.data}
                loading={runsQ.isLoading}
              />
            </Col>
            <Col xs={24} md={12}>
              <RunPicker
                label="Run B (right)"
                value={rightRunId}
                onChange={setRightRunId}
                runs={runsQ.data}
                loading={runsQ.isLoading}
              />
            </Col>
          </Row>

          {/* Summary cards */}
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <RunSummary run={leftQ.data} side="L" />
            </Col>
            <Col xs={24} md={12}>
              <RunSummary run={rightQ.data} side="R" />
            </Col>
          </Row>

          {/* Property comparison */}
          {(leftQ.data || rightQ.data) && (
            <Card
              title="Property-by-property comparison"
              style={{ marginTop: 16 }}
              styles={{ body: { padding: 0 } }}
            >
              <Table
                dataSource={rows}
                size="small"
                pagination={false}
                columns={[
                  {
                    title: '', dataIndex: 'label', width: 110,
                    render: (label: string, r) => (
                      <Space direction="vertical" size={0}>
                        <Text strong>{label}</Text>
                        {r.unit && <Text type="secondary" style={{ fontSize: 11 }}>{r.unit}</Text>}
                      </Space>
                    ),
                  },
                  // ── A (left) ──
                  {
                    title: <Tag color="blue">A · Mean</Tag>,
                    dataIndex: 'l_mean', align: 'right', width: 90,
                    render: (v, r) => (
                      <Text strong={r.better_side === 'L'}
                        style={r.better_side === 'L' ? { color: '#52c41a' } : undefined}>
                        {v}
                      </Text>
                    ),
                  },
                  {
                    title: <Tag color="blue">A · CV%</Tag>,
                    dataIndex: 'l_cv', align: 'right', width: 80,
                    render: (v, r) => (
                      <Text style={r.better_side === 'L' ? { color: '#52c41a', fontWeight: 600 } : undefined}>
                        {v === '—' ? '—' : `${v}%`}
                      </Text>
                    ),
                  },
                  // ── Δ ──
                  {
                    title: 'Δ Mean', align: 'center', width: 80,
                    render: (_, r) => {
                      if (r.delta_mean == null) return <Text type="secondary">—</Text>
                      const sign = r.delta_mean > 0 ? '+' : ''
                      return (
                        <Text style={{
                          color: Math.abs(r.delta_mean) < 0.1 ? '#888' : '#1677ff',
                          fontFamily: 'monospace', fontSize: 12,
                        }}>
                          {sign}{r.delta_mean.toFixed(2)}
                        </Text>
                      )
                    },
                  },
                  // ── B (right) ──
                  {
                    title: <Tag color="purple">B · Mean</Tag>,
                    dataIndex: 'r_mean', align: 'right', width: 90,
                    render: (v, r) => (
                      <Text strong={r.better_side === 'R'}
                        style={r.better_side === 'R' ? { color: '#52c41a' } : undefined}>
                        {v}
                      </Text>
                    ),
                  },
                  {
                    title: <Tag color="purple">B · CV%</Tag>,
                    dataIndex: 'r_cv', align: 'right', width: 80,
                    render: (v, r) => (
                      <Text style={r.better_side === 'R' ? { color: '#52c41a', fontWeight: 600 } : undefined}>
                        {v === '—' ? '—' : `${v}%`}
                      </Text>
                    ),
                  },
                  {
                    title: 'Better', align: 'center', width: 80,
                    render: (_, r) => r.better_side == null ? '—'
                      : r.better_side === 'TIE' ? <Tag>tie</Tag>
                      : <Tag color={r.better_side === 'L' ? 'blue' : 'purple'}>
                          {r.better_side === 'L' ? 'A' : 'B'}
                        </Tag>,
                  },
                ]}
              />
              <div style={{ padding: 12, background: '#fafafa', fontSize: 12 }}>
                <Text type="secondary">
                  <strong>Better</strong> = lower within-laydown CV% on that property.
                  Lower CV means tighter within-bale consistency, which usually
                  translates to fewer ring-frame breaks.
                </Text>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
